
import React, { useState } from "react";
import { Requirement } from "@/types/project";
import { Pencil, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RequirementType } from "@/components/project/RequirementType";
import { Loader2 } from "lucide-react";

interface StandardRequirementsTableProps {
  requirements: Requirement[];
  isLoading: boolean;
  onViewGuidance: (requirement: Requirement) => void;
  onEditRequirement: (requirement: Requirement) => void;
}

export function StandardRequirementsTable({
  requirements,
  isLoading,
  onViewGuidance,
  onEditRequirement,
}: StandardRequirementsTableProps) {
  return (
    <div className="bg-card border rounded-lg p-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">Standard Requirements</h2>
        <p className="text-sm text-muted-foreground">
          {requirements.length || 0} requirements
        </p>
      </div>
      
      {isLoading ? (
        <div className="py-8 text-center">
          <Loader2 className="h-8 w-8 mx-auto animate-spin text-muted-foreground" />
          <p className="mt-2 text-muted-foreground">Loading requirements...</p>
        </div>
      ) : requirements && requirements.length > 0 ? (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Section</TableHead>
                <TableHead>Subsection</TableHead>
                <TableHead className="w-1/3">Requirement</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Identifier</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requirements.map((req) => (
                <TableRow key={req.id} className="hover:bg-muted/50">
                  <TableCell>
                    {req.section || <span className="text-muted-foreground text-xs">-</span>}
                  </TableCell>
                  <TableCell>
                    {req.subsection || <span className="text-muted-foreground text-xs">-</span>}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">
                      {req.requirement_text || req.text || <span className="text-muted-foreground text-xs">-</span>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <RequirementType type={req.requirement_type || req.type} />
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs">
                      {req.identifier || <span className="text-muted-foreground">-</span>}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end space-x-2">
                      <Button 
                        variant="outline" 
                        size="icon" 
                        onClick={() => onViewGuidance(req)}
                        title="View guidance"
                      >
                        <Info className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="icon" 
                        onClick={() => onEditRequirement(req)}
                        title="Edit requirement"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="py-8 text-center text-muted-foreground border rounded-md bg-muted/40">
          No requirements found for this standard.
        </div>
      )}
    </div>
  );
}
